import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

const STORAGE_KEY = 'nico-a11y-prefs'

export const DEFAULTS = {
  theme: 'auto', // 'auto' | 'light' | 'dark'
  reduceMotion: false,
  grayscale: false,
  highContrast: false,
  largerText: false,
  underlineLinks: false,
  disableAmbience: false,
}

function readFromStorage() {
  if (typeof window === 'undefined') return DEFAULTS
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    const parsed = JSON.parse(raw)
    return { ...DEFAULTS, ...parsed }
  } catch {
    return DEFAULTS
  }
}

function writeToStorage(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {}
}

function applyToDocument(settings) {
  if (typeof document === 'undefined') return
  const root = document.documentElement

  if (settings.theme === 'dark' || settings.theme === 'light') {
    root.setAttribute('data-user-theme', settings.theme)
    root.setAttribute('data-theme', settings.theme)
  } else {
    root.removeAttribute('data-user-theme')
    root.removeAttribute('data-theme')
  }

  const toggleAttr = (attr, on) => {
    if (on) root.setAttribute(attr, 'true')
    else root.removeAttribute(attr)
  }

  toggleAttr('data-reduced-motion', settings.reduceMotion)
  toggleAttr('data-grayscale', settings.grayscale)
  toggleAttr('data-high-contrast', settings.highContrast)
  toggleAttr('data-larger-text', settings.largerText)
  toggleAttr('data-underline-links', settings.underlineLinks)
  toggleAttr('data-disable-ambience', settings.disableAmbience)
}

const AccessibilityContext = createContext(null)

export function AccessibilityProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS)

  // Hydrate from storage after mount. SSR render uses DEFAULTS which matches
  // the status quo, so no part of the React tree differs between server and
  // client. The inline preload script in gatsby-ssr.js has already set the
  // <html> data-* attributes before paint, so this effect is mostly a no-op.
  useEffect(() => {
    const stored = readFromStorage()
    setSettings(stored)
    applyToDocument(stored)
  }, [])

  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: value }
      writeToStorage(next)
      applyToDocument(next)
      return next
    })
  }, [])

  const resetSettings = useCallback(() => {
    setSettings(DEFAULTS)
    writeToStorage(DEFAULTS)
    applyToDocument(DEFAULTS)
  }, [])

  return (
    <AccessibilityContext.Provider
      value={{ settings, updateSetting, resetSettings }}
    >
      {children}
    </AccessibilityContext.Provider>
  )
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext)
  if (!ctx) {
    // Safe fallback if used outside provider (e.g. during SSR of an isolated
    // component). Returns defaults + no-op setters.
    return {
      settings: DEFAULTS,
      updateSetting: () => {},
      resetSettings: () => {},
    }
  }
  return ctx
}
